import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2, XCircle, AlertTriangle, PlugZap, Loader2, RefreshCw,
  ShieldCheck,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  PASS: { icon: CheckCircle2, cls: "text-lifted-green", label: "Pass",
          bg: "bg-lifted-green/10 border-lifted-green/30" },
  PARTIAL: { icon: AlertTriangle, cls: "text-amber-300", label: "Partial",
             bg: "bg-amber-500/10 border-amber-500/30" },
  CONNECTION_REQUIRED: { icon: PlugZap, cls: "text-sky-300",
                          label: "Connection req'd",
                          bg: "bg-sky-500/10 border-sky-400/30" },
  ERROR: { icon: XCircle, cls: "text-red-300", label: "Error",
           bg: "bg-red-500/10 border-red-500/30" },
};

// Group rows into logical sections — Master Admin command center.
const SECTIONS = [
  {
    key: "shop",
    label: "Lifted Main Shop",
    keys: ["catalog", "media", "pricing", "products_unified",
           "native_inventory_editor", "shopify_independence"],
  },
  {
    key: "affiliate",
    label: "Affiliate & Lift-Off",
    keys: ["commissions", "affiliate_tracking", "affiliate_zone_ui",
           "affiliate_payment_controls", "affiliate_billing_migration",
           "affiliate_stripe_recurring", "affiliate_product_links",
           "affiliate_same_day_links", "affiliate_qr_generator",
           "affiliate_sale_tracking", "affiliate_messages",
           "affiliate_product_link_generator", "affiliate_product_picker_images",
           "affiliate_free_access_toggle"],
  },
  {
    key: "stripe",
    label: "Stripe Payments",
    keys: ["stripe", "stripe_webhook", "stripe_checkout",
           "payment", "checkout_destination_aware"],
  },
  {
    key: "compliance",
    label: "Compliance & AI",
    keys: ["compliance", "restrictions"],
  },
  {
    key: "supplies",
    label: "Lifted Supplies",
    keys: ["same_day", "delivery_engine", "driver", "tomtom"],
  },
  {
    key: "training",
    label: "Training & Certification",
    keys: ["training", "certification", "training_role_curricula",
           "affiliate_training_context", "public_certificates"],
  },
  {
    key: "partner",
    label: "Partner & Franchise",
    keys: ["partner", "franchise"],
  },
  {
    key: "comms",
    label: "Email · SMS · Notifications",
    keys: ["email_sms", "messaging", "network", "network_feed",
           "network_post_lifecycle"],
  },
  {
    key: "platform",
    label: "Platform Health",
    keys: ["auth", "uploads", "menu_navigation", "sidebar_dropdown_behavior",
           "role_context_switching", "universal_profile_avatar",
           "shopify_uppromote_ui_dependency", "uppromote_sync",
           "tapstitch", "product_image_visibility"],
  },
];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.ERROR;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border ${meta.bg}`}
      data-testid={`status-${(status || "").toLowerCase()}`}
    >
      <Icon size={11} className={meta.cls} />
      <span className={meta.cls}>{meta.label}</span>
    </span>
  );
}

export default function AdminSystemReadiness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/api/admin/system-readiness`, {
        credentials: "include",
      });
      if (!r.ok) {
        setError(`Backend returned ${r.status}. Some checks may have failed individually — partial data shown below.`);
        try { setData(await r.json()); } catch { /* ignore */ }
      } else {
        setData(await r.json());
      }
    } catch (e) {
      setError("Could not reach backend. Try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    if (!data?.rows) return {};
    const used = new Set();
    const out = {};
    for (const sec of SECTIONS) {
      out[sec.key] = data.rows.filter((r) => {
        if (sec.keys.includes(r.key)) { used.add(r.key); return true; }
        return false;
      });
    }
    // Anything that fell through goes into "Other"
    out._other = data.rows.filter((r) => !used.has(r.key));
    return out;
  }, [data]);

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="admin-system-readiness">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-lifted-green mb-2">
            Master Admin · System Health
          </div>
          <h1 className="font-anton uppercase text-3xl sm:text-4xl text-white">
            System Readiness
          </h1>
          <p className="text-lifted-dim text-sm mt-2 max-w-2xl">
            Every subsystem the LiftedAtTheMoment ecosystem touches. A single
            failed check never crashes the page — partial data is always shown.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded bg-lifted-green text-black text-xs font-bebas tracking-wider uppercase hover:bg-lifted-green/90 disabled:opacity-50"
          data-testid="run-full-readiness-check"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Run full check
        </button>
      </div>

      {error && (
        <div
          className="glass-panel p-4 mb-6 text-amber-200 border border-amber-400/30 text-sm"
          data-testid="readiness-error-banner"
        >
          {error}
        </div>
      )}

      {data && (
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6"
          data-testid="readiness-summary"
        >
          <SummaryCard label="Pass" value={data.summary?.PASS || 0} kind="PASS" />
          <SummaryCard label="Partial" value={data.summary?.PARTIAL || 0} kind="PARTIAL" />
          <SummaryCard label="Connection req'd" value={data.summary?.CONNECTION_REQUIRED || 0} kind="CONNECTION_REQUIRED" />
          <SummaryCard label="Errors" value={data.summary?.ERROR || 0} kind="ERROR" />
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-lifted-dim" data-testid="readiness-loading">
          <Loader2 size={16} className="animate-spin" /> Probing every subsystem…
        </div>
      )}

      {SECTIONS.map((sec) => {
        const rows = grouped[sec.key] || [];
        if (!rows.length) return null;
        return (
          <section key={sec.key} className="mb-6" data-testid={`readiness-section-${sec.key}`}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-bebas tracking-[0.2em] text-lifted-green text-lg uppercase">
                {sec.label} <span className="text-lifted-dim text-xs ml-2">({rows.length})</span>
              </h2>
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-white/10 text-lifted-dim hover:text-white disabled:opacity-50"
                data-testid={`recheck-${sec.key}`}
              >
                <RefreshCw size={10} className="inline mr-1" /> Recheck
              </button>
            </div>
            <div className="glass-panel divide-y divide-white/5">
              {rows.map((row) => (
                <div
                  key={row.key}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 p-3"
                  data-testid={`readiness-row-${row.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm text-white">{row.label}</p>
                    <p className="text-xs text-lifted-dim mt-0.5">{row.detail}</p>
                    {row.missing_credentials?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {row.missing_credentials.map((c) => (
                          <span
                            key={c}
                            className="text-[10px] font-mono bg-white/5 text-amber-200 border border-amber-400/20 px-1.5 py-0.5 rounded"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <StatusBadge status={row.status} />
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {grouped._other?.length > 0 && (
        <section className="mb-6">
          <h2 className="font-bebas tracking-[0.2em] text-lifted-dim text-lg uppercase mb-2">
            Other
          </h2>
          <div className="glass-panel divide-y divide-white/5">
            {grouped._other.map((row) => (
              <div key={row.key} className="flex items-center gap-2 p-3">
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-sm text-white">{row.label}</p>
                  <p className="text-xs text-lifted-dim mt-0.5">{row.detail}</p>
                </div>
                <StatusBadge status={row.status} />
              </div>
            ))}
          </div>
        </section>
      )}

      {data?.generated_at && (
        <div className="mt-6 text-xs text-lifted-dim flex items-center gap-2">
          <ShieldCheck size={12} className="text-lifted-green" />
          Last evaluated <span className="text-lifted-green">{new Date(data.generated_at).toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, kind }) {
  const meta = STATUS_META[kind] || STATUS_META.ERROR;
  return (
    <div className={`glass-panel p-3 border ${meta.bg}`} data-testid={`summary-${kind.toLowerCase()}`}>
      <p className={`text-[10px] uppercase tracking-[0.15em] ${meta.cls}`}>{label}</p>
      <p className={`font-anton text-3xl ${meta.cls}`}>{value}</p>
    </div>
  );
}
